import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";
import { getInitialLang, setLang as persistLang } from "./i18n";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lang, setLangState] = useState(getInitialLang());

    const setLang = useCallback((l) => {
        persistLang(l);
        setLangState(l);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Heartbeat every 30s to keep last_seen fresh
    useEffect(() => {
        if (!user) return;
        const t = setInterval(() => {
            api.post("/heartbeat").catch(() => {});
        }, 30000);
        return () => clearInterval(t);
    }, [user]);

    const login = async (email, password, totpCode, backupCode) => {
        const payload = { email, password };
        if (totpCode) payload.totp_code = totpCode;
        if (backupCode) payload.backup_code = backupCode;
        const { data } = await api.post("/auth/login", payload);
        if (data.token) {
            localStorage.setItem("taskflow_token", data.token);
        }
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch { /* ignore */ }
        localStorage.removeItem("taskflow_token");
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, logout, refresh, lang, setLang, setUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}
